import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import polylabel from "polylabel";
import areapolygon from "area-polygon";
import { connect } from "react-redux";
import { fraLog } from "../../utils/fra-debug";

const STYLE_TEXT = {
  textAnchor: "middle",
  fontFamily: '"Courier New", Courier, monospace',
  pointerEvents: "none",
  fontWeight: "bold",

  // http://stackoverflow.com/questions/826782/how-to-disable-text-selection-highlighting-using-css
  WebkitTouchCallout: "none" /* iOS Safari */,
  WebkitUserSelect: "none" /* Chrome/Safari/Opera */,
  MozUserSelect: "none" /* Firefox */,
  MsUserSelect: "none" /* Internet Explorer/Edge */,
  userSelect: "none",
};

const STYLE_DIMENSION_TEXT = {
  ...STYLE_TEXT,
  fill: "#1a1a1a",
  paintOrder: "stroke fill",
  stroke: "#ffffff",
  strokeWidth: 7,
  strokeLinejoin: "round",
  strokeLinecap: "round",
};
const filterVerticesWithDuplication = (selectedAreaId, areas) => {
  const selectedVertices = areas.get(selectedAreaId).get("vertices");
  let filteredVertices = new Map();

  areas.forEach((area, areaId) => {
    if (areaId !== selectedAreaId) {
      const areaVertices = area.get("vertices");
      selectedVertices.forEach((vertex) => {
        if (areaVertices.includes(vertex)) {
          filteredVertices = filteredVertices.set(vertex, true);
        }
      });
    }
  });

  return selectedVertices
    .filter((vertex) => !filteredVertices.has(vertex))
    .toList();
};
const Area = function ({ layer, area, catalog, state, labelsOnly }) {
  const [splitRoomName, setSplitRoomName] = useState([]);
  // console.log(area);
  // useEffect(() => {
  //   // Function to split roomName into substrings based on available width
  //   const splitRoomNameIntoArray = (roomName, availableWidth) => {
  //     const words = roomName.split(" ");
  //     const maxCharsPerLine = Math.floor(availableWidth / 12); // Assuming font-size of 12px per character
  //     let lines = [];
  //     let currentLine = "";

  //     words.forEach((word, index) => {
  //       if (currentLine.length === 0) {
  //         currentLine = word;
  //       } else {
  //         const potentialLine = currentLine + " " + word;
  //         if (potentialLine.length <= maxCharsPerLine) {
  //           currentLine = potentialLine;
  //         } else {
  //           lines.push(currentLine);
  //           currentLine = word;
  //         }
  //       }

  //       // Handle the last word separately
  //       if (index === words.length - 1) {
  //         lines.push(currentLine);
  //       }
  //     });
  //
  //     // Split lines longer than maxCharsPerLine into chunks
  //     let chunkedLines = [];
  //     lines.forEach(line => {
  //       if (line.length <= maxCharsPerLine) {
  //         chunkedLines.push(line);
  //       } else {
  //         let i = 0;
  //         while (i < line.length) {
  //           chunkedLines.push(line.slice(i, i + maxCharsPerLine));
  //           i += maxCharsPerLine;
  //         }
  //       }
  //     });

  //     return chunkedLines;
  //   };

  //   // Calculate available width for displaying the room name
  //   let polygon = area.vertices.toArray().map((vertexID) => {
  //     let { x, y } = layer.vertices.get(vertexID);
  //     return [x, y];
  //   });

  //   let polygonWithHoles = polygon;
  //   area.holes.forEach((holeID) => {
  //     let polygonHole = layer.areas
  //       .get(holeID)
  //       .vertices.toArray()
  //       .map((vertexID) => {
  //         let { x, y } = layer.vertices.get(vertexID);
  //         return [x / 100, y / 100];
  //       });

  //     polygonWithHoles = polygonWithHoles.concat(polygonHole.reverse());
  //   });

  //   let { width } = calculateBoundingBox(polygonWithHoles, 1);

  //   const availableWidth = width / 100; // Convert to meters if necessary
  //   const lines = splitRoomNameIntoArray(area.properties.get("roomName"), availableWidth);

  //   setSplitRoomName(lines);
  // }, [area, catalog, layer]);

  let renderedAreaSize = null;

  // Build vertex arrays with null safety
  let polygon = area.vertices.toArray()
    .map((vertexID) => {
      let vertex = layer.vertices.get(vertexID);
      if (!vertex) return null;
      return [vertex.x, vertex.y];
    })
    .filter(v => v !== null);

  // Always paint solid white from the same points as the dimension label.
  // Catalog path alone was leaving some rooms transparent after split/drag.
  let rendered = null;
  if (polygon.length >= 3) {
    let signed = 0;
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      signed += polygon[i][0] * polygon[j][1] - polygon[j][0] * polygon[i][1];
    }
    const absArea = Math.abs(signed / 2);
    const xs = polygon.map((p) => p[0]);
    const ys = polygon.map((p) => p[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const bboxArea = Math.max(0, maxX - minX) * Math.max(0, maxY - minY);

    let fillPath;
    if (bboxArea > 1 && absArea < bboxArea * 0.35) {
      // Self-crossing / bad cycle → solid room box (matches label bounds).
      fillPath = `M${minX} ${minY} L${maxX} ${minY} L${maxX} ${maxY} L${minX} ${maxY} Z`;
    } else {
      const ordered = signed > 0 ? polygon.slice().reverse() : polygon;
      fillPath =
        ordered
          .map((p, ind) => `${ind ? "L" : "M"}${p[0]} ${p[1]}`)
          .join(" ") + " Z";
    }
    rendered = (
      <path
        d={fillPath}
        fill="#ffffff"
        fillOpacity={1}
        opacity={1}
        fillRule="nonzero"
        stroke="none"
      />
    );
  }

  let polygon2 = area.vertices.toArray()
    .map((vertexID) => {
      let vertex = layer.vertices.get(vertexID);
      if (!vertex) return null;
      return { x: vertex.x, y: vertex.y };
    })
    .filter(v => v !== null);

  // Build polygonWithHoles with null safety
  let polygonWithHoles = [...polygon];
  area.holes.forEach((holeID) => {
    let holeArea = layer.areas.get(holeID);
    if (!holeArea) return;
    let polygonHole = holeArea.vertices.toArray()
      .map((vertexID) => {
        let vertex = layer.vertices.get(vertexID);
        if (!vertex) return null;
        return [vertex.x, vertex.y];
      })
      .filter(v => v !== null);
    polygonWithHoles = polygonWithHoles.concat(polygonHole.reverse());
  });

  let areaSize = areapolygon(polygon, false);
  area.holes.forEach((areaID) => {
    let hole = layer.areas.get(areaID);
    if (!hole) return; // skip missing holes
    let holePolygon = hole.vertices.toArray()
      .map((vertexID) => {
        let vertex = layer.vertices.get(vertexID);
        if (!vertex) return null;
        return [vertex.x, vertex.y];
      })
      .filter(v => v !== null);
    areaSize -= areapolygon(holePolygon, false);
  });

  // FIX: calculateBoundingBox on outer polygon only (dedupe collinear verts)
  const polygonForBounds = dedupePolygonVertices(polygon);
  const boundsSource = polygonForBounds.length >= 3 ? polygonForBounds : polygon;
  let { width, height } = calculateBoundingBox(boundsSource, 1);
  const grossArea = Math.abs(areapolygon(boundsSource, false));
  if (width <= 0 && grossArea > 0) {
    width = Math.sqrt(grossArea);
  }
  if (height <= 0 && grossArea > 0) {
    height = Math.sqrt(grossArea);
  }
  if ((width <= 0 || height <= 0) && boundsSource.length >= 3) {
    const xs = boundsSource.map(([x]) => x);
    const ys = boundsSource.map(([, y]) => y);
    width = Math.max(width, Math.max(...xs) - Math.min(...xs));
    height = Math.max(height, Math.max(...ys) - Math.min(...ys));
  }

  const displayWidth =
    width > 0 ? width : Math.max(...polygon.map(([x]) => x)) - Math.min(...polygon.map(([x]) => x));
  const displayHeight =
    height > 0 ? height : Math.max(...polygon.map(([, y]) => y)) - Math.min(...polygon.map(([, y]) => y));

  const hasValidDimensions =
    polygon.length >= 3 &&
    (grossArea > 0 || displayWidth > 0 || displayHeight > 0);

  const labelPolygon =
    polygon.length >= 3 ? dedupePolygonVertices(polygon) : boundsSource;
  const labelPos = computeAreaLabelPosition(labelPolygon, area.holes, layer);

  const minSide = Math.min(displayWidth, displayHeight);
  const aspectRatio =
    minSide > 0
      ? Math.max(displayWidth, displayHeight) / minSide
      : 1;
  const isNarrowRoom = minSide > 0 && (minSide < 280 || aspectRatio > 3);

  useEffect(() => {
    // Function to split roomName into chunks of up to 8 characters each
    const splitRoomNameIntoChunks = (roomName) => {
      const maxCharsPerChunk = Math.max(8, Math.floor(width / 20) || 8);
      const chunks = [];
      let startIndex = 0;

      while (startIndex < roomName.length) {
        const chunk = roomName.slice(startIndex, startIndex + maxCharsPerChunk);
        chunks.push(chunk);
        startIndex += maxCharsPerChunk;
      }

      return chunks;
    };
    const roomName = area.properties.get("roomName");
    const chunks = splitRoomNameIntoChunks(roomName);

    // Replace with your state handling logic
    setSplitRoomName(chunks);
  }, [area, width]);
  // FIX: safely get viewer zoom - viewer2D.a can be undefined for floors
  // that were never viewed in drawing mode, causing NaN font size = invisible text
  const planner =
    state.getIn && state.getIn(["react-planner"])
      ? state.getIn(["react-planner"])
      : state;
  const viewer2DObj = planner && planner.viewer2D;
  const viewer2DJs = viewer2DObj && viewer2DObj.toJS ? viewer2DObj.toJS() : {};
  const viewerA = (viewer2DJs && viewer2DJs.a) || 0.5;
  const roomMinSide = Math.max(minSide, 80);
  const baseFont = isNarrowRoom
    ? Math.max(7, Math.min(12, roomMinSide / 14))
    : Math.max(8, Math.min(15, roomMinSide / 17));
  const dimensionFontSize = `${
    baseFont / viewerA < baseFont
      ? baseFont
      : baseFont / (viewerA < 0.3 ? viewerA * 1.5 : viewerA)
  }px`;
  const useStackedDimensions =
    minSide > 0 &&
    ((displayWidth < displayHeight && displayWidth < 520) ||
      (displayHeight < displayWidth && displayHeight < 520) ||
      aspectRatio > 2.5);
  const labelRotation =
    !useStackedDimensions && isNarrowRoom && displayWidth < displayHeight
      ? -90
      : 0;
  const showDimensions =
    !planner || planner.get("dimension") !== false;
  const isEnvelopeShell = areaIsEnvelopeShell(area, layer);
  const isNearDupLabel = isNearDuplicateDimensionArea(area, layer);
  const showDimensionLabel =
    showDimensions &&
    hasValidDimensions &&
    !isEnvelopeShell &&
    !isNearDupLabel;
  const widthM = (displayWidth / 100).toFixed(2);
  const heightM = (displayHeight / 100).toFixed(2);
  const dimensionLabelBg = {
    width: Math.max(
      88,
      Math.min(displayWidth * 0.82, (`${widthM}m x ${heightM}m`.length + 2) * 8.5)
    ),
    height: useStackedDimensions ? baseFont * 4.4 : baseFont * 2.1,
  };

  if (labelsOnly && !showDimensionLabel) {
    fraLog("DIM", "No dimension label for this room", {
      areaId: area.id,
      hasValidDimensions,
      showDimensions,
      polygonVerts: polygon.length,
      approxM: `${widthM} x ${heightM}`,
      minSide,
    });
  }

  renderedAreaSize = (
    <Fragment>
      {splitRoomName.map((line, index) => (
        <text
          key={index}
          display="block"
          x="0"
          y={index === 0 ? 0 : index * 20}
          transform={`translate(${labelPos.x} ${labelPos.y + 20}) scale(1, -1)`}
          style={{
            ...STYLE_TEXT,
            fontSize: dimensionFontSize,
            whiteSpace: "pre-line",
            textAlign: "center",
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            fontWeight: 100,
          }}
        >
          {line}
        </text>
      ))}
      {showDimensionLabel && useStackedDimensions && (
        <g transform={`translate(${labelPos.x} ${labelPos.y})`}>
          <rect
            x={-dimensionLabelBg.width / 2}
            y={-dimensionLabelBg.height / 2}
            width={dimensionLabelBg.width}
            height={dimensionLabelBg.height}
            fill="#ffffff"
            fillOpacity={0.96}
            rx={4}
            ry={4}
            stroke="none"
            pointerEvents="none"
          />
          <text
            display="block"
            x="0"
            y={splitRoomName.length * 20}
            transform="scale(1, -1)"
            style={{
              ...STYLE_DIMENSION_TEXT,
              fontSize: dimensionFontSize,
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            }}
          >
            <tspan x="0" dy="0">
              {widthM}m
            </tspan>
            <tspan x="0" dy="1.15em">
              x
            </tspan>
            <tspan x="0" dy="1.15em">
              {heightM}m
            </tspan>
          </text>
        </g>
      )}
      {showDimensionLabel && !useStackedDimensions && (
        <g transform={`translate(${labelPos.x} ${labelPos.y})`}>
          <rect
            x={-dimensionLabelBg.width / 2}
            y={-dimensionLabelBg.height / 2}
            width={dimensionLabelBg.width}
            height={dimensionLabelBg.height}
            fill="#ffffff"
            fillOpacity={0.96}
            rx={4}
            ry={4}
            stroke="none"
            pointerEvents="none"
          />
          <text
            display="block"
            x="0"
            y={splitRoomName.length * 20}
            transform={`rotate(${labelRotation}) scale(1, -1)`}
            style={{
              ...STYLE_DIMENSION_TEXT,
              fontSize: dimensionFontSize,
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            }}
          >
            {widthM}m x {heightM}m
          </text>
        </g>
      )}
    </Fragment>
  );

  // When labelsOnly=true, render ONLY dimension labels (no fill/shape)
  // Used by Layer to render labels on top of all items/stairs
  if (labelsOnly) {
    if (polygon.length < 3) {
      return <g data-layer={layer.id} />;
    }
    return (
      <g data-layer={layer.id} data-area-label={area.id} style={{ pointerEvents: "none" }}>
        {renderedAreaSize}
      </g>
    );
  }

  if (polygon.length < 3) {
    return null;
  }

  return (
    <g
      data-element-root
      data-prototype={area.prototype}
      data-id={area.id}
      data-selected={area.selected}
      data-layer={layer.id}
    >
      {rendered}
      {/* labelsOnly=false: skip labels here, they render in pass 2 via layer.jsx */}
    </g>
  );
};

Area.propTypes = {
  area: PropTypes.object.isRequired,
  layer: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

function mapStateToProps(reduxState) {
  return {
    state: reduxState,
  };
}

export default connect(mapStateToProps)(Area);

function getPolygonCentroid(pathData) {
  // Split the SVG path data into individual commands
  var commands = pathData.split(/(?=[A-Z])/);

  // Initialize variables for centroid calculation
  var area = 0;
  var cx = 0;
  var cy = 0;

  // Iterate over the commands to calculate the centroid
  for (var i = 0; i < commands.length; i++) {
    var command = commands[i];
    var params = command.substring(1).trim().split(/[ ,]+/);

    // Extract the coordinates of the vertices
    var x = parseFloat(params[params.length - 2]);
    var y = parseFloat(params[params.length - 1]);

    // Calculate the area of the polygon using the shoelace formula
    var nextParams = commands[(i + 1) % commands.length]
      .substring(1)
      .trim()
      .split(/[ ,]+/);
    var nextX = parseFloat(nextParams[nextParams.length - 2]);
    var nextY = parseFloat(nextParams[nextParams.length - 1]);
    area += x * nextY - nextX * y;

    // Calculate the centroid using the centroid formula
    cx += (x + nextX) * (x * nextY - nextX * y);
    cy += (y + nextY) * (x * nextY - nextX * y);
  }

  // Divide by 6 times the polygon's area to get the centroid coordinates
  area /= 2;
  cx /= 6 * area;
  cy /= 6 * area;

  return { x: cx, y: cy };
}

function getPolygonCentroidByVertexPositions(vertices) {
  const n = vertices.length;

  if (n < 4) {
    // Never throw during render — white screen. Fall back to simple average.
    if (!n) return { x: 0, y: 0 };
    let Cx = 0,
      Cy = 0;
    for (const vertex of vertices) {
      Cx += vertex.x;
      Cy += vertex.y;
    }
    return { x: Cx / n, y: Cy / n };
  }

  // Calculate distances between every pair of vertices
  const distances = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const distance = Math.sqrt(
        Math.pow(vertices[i].x - vertices[j].x, 2) +
          Math.pow(vertices[i].y - vertices[j].y, 2)
      );
      distances.push({ i, j, distance });
    }
  }

  // Sort distances by ascending order
  distances.sort((a, b) => a.distance - b.distance);

  // Select the closest four vertices
  const vertexSet = new Set();
  let k = 0;
  while (vertexSet.size < 4 && k < distances.length) {
    vertexSet.add(distances[k].i);
    vertexSet.add(distances[k].j);
    k++;
  }

  // Extract the selected four vertices
  const selectedVertices = Array.from(vertexSet).map(
    (index) => vertices[index]
  );

  // Calculate the centroid of the selected vertices
  let Cx = 0,
    Cy = 0;
  for (const vertex of selectedVertices) {
    Cx += vertex.x;
    Cy += vertex.y;
  }
  Cx /= selectedVertices.length;
  Cy /= selectedVertices.length;

  return {
    x: Cx,
    y: Cy,
  };
}

function getPolygonCentroidByFarthestVertices(vertices) {
  const n = vertices.length;

  if (n < 4) {
    if (!n) return { x: 0, y: 0 };
    let Cx = 0,
      Cy = 0;
    for (const vertex of vertices) {
      Cx += vertex.x;
      Cy += vertex.y;
    }
    return { x: Cx / n, y: Cy / n };
  }

  // Calculate distances between every pair of vertices
  const distances = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const distance = Math.sqrt(
        Math.pow(vertices[i].x - vertices[j].x, 2) +
          Math.pow(vertices[i].y - vertices[j].y, 2)
      );
      distances.push({ i, j, distance });
    }
  }

  // Sort distances by descending order to find the farthest pairs
  distances.sort((a, b) => b.distance - a.distance);

  // Select the farthest four vertices
  const vertexSet = new Set();
  let k = 0;
  while (vertexSet.size < 4 && k < distances.length) {
    vertexSet.add(distances[k].i);
    vertexSet.add(distances[k].j);
    k++;
  }

  // Extract the selected four vertices
  const selectedVertices = Array.from(vertexSet).map(
    (index) => vertices[index]
  );

  // Calculate the centroid of the selected vertices
  let Cx = 0,
    Cy = 0;
  for (const vertex of selectedVertices) {
    Cx += vertex.x;
    Cy += vertex.y;
  }
  Cx /= selectedVertices.length;
  Cy /= selectedVertices.length;

  return {
    x: Cx,
    y: Cy,
  };
}

function getPolygonCentroidByModerateVertices(vertices) {
  const n = vertices.length;

  if (n < 4) {
    if (!n) return { x: 0, y: 0 };
    let Cx = 0,
      Cy = 0;
    for (const vertex of vertices) {
      Cx += vertex.x;
      Cy += vertex.y;
    }
    return { x: Cx / n, y: Cy / n };
  }

  // Calculate distances between every pair of vertices
  const distances = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const distance = Math.sqrt(
        Math.pow(vertices[i].x - vertices[j].x, 2) +
          Math.pow(vertices[i].y - vertices[j].y, 2)
      );
      distances.push({ i, j, distance });
    }
  }

  // Sort distances by ascending order
  distances.sort((a, b) => a.distance - b.distance);

  // Find the median distance index
  const medianIndex = Math.floor(distances.length / 2);

  // Select vertices around the median distance
  const vertexSet = new Set();
  let k = medianIndex;
  while (vertexSet.size < 4 && k < distances.length) {
    vertexSet.add(distances[k].i);
    vertexSet.add(distances[k].j);
    k++;
  }

  // Ensure we have 4 vertices (backtrack if needed)
  k = medianIndex - 1;
  while (vertexSet.size < 4 && k >= 0) {
    vertexSet.add(distances[k].i);
    vertexSet.add(distances[k].j);
    k--;
  }

  // Extract the selected four vertices
  const selectedVertices = Array.from(vertexSet).map(
    (index) => vertices[index]
  );

  // Calculate the centroid of the selected vertices
  let Cx = 0,
    Cy = 0;
  for (const vertex of selectedVertices) {
    Cx += vertex.x;
    Cy += vertex.y;
  }
  Cx /= selectedVertices.length;
  Cy /= selectedVertices.length;

  return {
    x: Cx,
    y: Cy,
  };
}

// another solution
function getSafeLabelPosition(vertices) {
  // Calculate the total area of the polygon
  const totalArea = calculatePolygonArea(vertices);

  // Define the percentages to try
  const percentages = [40, 20, 10];

  // Try each percentage in decreasing order
  for (const percentage of percentages) {
    const minArea = (percentage / 100) * totalArea;

    const candidatePoints = generateCandidatePoints(vertices, 10); // Adjust step size as needed
    const validPoints = candidatePoints.filter((point) =>
      isPointInPolygon(point, vertices)
    );

    if (validPoints.length === 0) {
      continue; // No valid points found; try the next percentage
    }

    let bestPoint = null;
    let maxArea = -Infinity;

    for (const point of validPoints) {
      const area = calculateEmptyAreaAroundPoint(point, vertices);
      if (area >= minArea && area > maxArea) {
        maxArea = area;
        bestPoint = point;
      }
    }

    if (bestPoint) {
      return bestPoint;
    }
  }

  // If no suitable area was found with any percentage, return the centroid of the polygon
  return calculatePolygonCentroid(vertices);
}

function generateCandidatePoints(vertices, stepSize) {
  const bounds = getPolygonBounds(vertices);
  const points = [];

  for (let x = bounds.minX; x <= bounds.maxX; x += stepSize) {
    for (let y = bounds.minY; y <= bounds.maxY; y += stepSize) {
      points.push({ x, y });
    }
  }

  return points;
}

function calculatePolygonArea(vertices) {
  let area = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }

  area = Math.abs(area) / 2;
  return area;
}

function calculateEmptyAreaAroundPoint(point, vertices) {
  // Placeholder function for empty area calculation
  // Implement actual logic if needed
  return Math.random() * 1000; // Replace with actual computation
}

function getPolygonBounds(vertices) {
  const minX = Math.min(...vertices.map((v) => v.x));
  const minY = Math.min(...vertices.map((v) => v.y));
  const maxX = Math.max(...vertices.map((v) => v.x));
  const maxY = Math.max(...vertices.map((v) => v.y));
  return { minX, minY, maxX, maxY };
}

function isPointInPolygon(point, vertices) {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i].x,
      yi = vertices[i].y;
    const xj = vertices[j].x,
      yj = vertices[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function calculatePolygonCentroid(vertices) {
  let Cx = 0,
    Cy = 0;
  const n = vertices.length;
  let signedArea = 0;

  for (let i = 0; i < n; i++) {
    const x0 = vertices[i].x;
    const y0 = vertices[i].y;
    const x1 = vertices[(i + 1) % n].x;
    const y1 = vertices[(i + 1) % n].y;
    const a = x0 * y1 - x1 * y0;
    signedArea += a;
    Cx += (x0 + x1) * a;
    Cy += (y0 + y1) * a;
  }

  signedArea *= 0.5;
  Cx /= 6 * signedArea;
  Cy /= 6 * signedArea;

  return { x: Cx, y: Cy };
}

//

function findPolygonCenter(vertices) {
  // Initialize sum for x and y coordinates
  let sumX = 0;
  let sumY = 0;

  // Calculate sum of x and y coordinates
  for (let i = 0; i < vertices.length; i++) {
    sumX += vertices[i][0];
    sumY += vertices[i][1];
  }

  // Calculate average for x and y coordinates
  const centerX = sumX / vertices.length;
  const centerY = sumY / vertices.length;

  return [centerX, centerY];
}

function computeBoundingBoxCenter(vertices) {
  if (!vertices || vertices.length === 0) {
    return { x: 0, y: 0 };
  }
  const xs = vertices.map(([x]) => x);
  const ys = vertices.map(([, y]) => y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

function dedupePolygonVertices(vertices) {
  if (!vertices || vertices.length < 2) return vertices || [];

  const result = [vertices[0]];
  for (let i = 1; i < vertices.length; i++) {
    const [x1, y1] = result[result.length - 1];
    const [x2, y2] = vertices[i];
    if (Math.hypot(x2 - x1, y2 - y1) > 0.5) {
      result.push(vertices[i]);
    }
  }

  const [fx, fy] = result[0];
  const [lx, ly] = result[result.length - 1];
  if (result.length > 1 && Math.hypot(fx - lx, fy - ly) <= 0.5) {
    result.pop();
  }

  return result.length >= 3 ? result : vertices;
}

function distancePointToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    return Math.hypot(px - x1, py - y1);
  }
  const t = Math.max(
    0,
    Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy))
  );
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return Math.hypot(px - projX, py - projY);
}

function minDistanceToPolygonEdges(point, polygon) {
  let minDist = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const [x1, y1] = polygon[i];
    const [x2, y2] = polygon[(i + 1) % polygon.length];
    minDist = Math.min(
      minDist,
      distancePointToSegment(point.x, point.y, x1, y1, x2, y2)
    );
  }
  return minDist;
}

function computeAreaLabelPosition(outerPolygon, holeIds, layer) {
  if (!outerPolygon || outerPolygon.length < 3) {
    return { x: 0, y: 0 };
  }

  const polygonVertices = outerPolygon.map(([px, py]) => ({ x: px, y: py }));
  const bboxCenter = computeBoundingBoxCenter(outerPolygon);

  const rings = [outerPolygon.map(([x, y]) => [x, y])];

  holeIds.forEach((holeID) => {
    const holeArea = layer.areas.get(holeID);
    if (!holeArea) return;

    const holeRing = holeArea.vertices
      .toArray()
      .map((vertexID) => {
        const vertex = layer.vertices.get(vertexID);
        if (!vertex) return null;
        return [vertex.x, vertex.y];
      })
      .filter((v) => v !== null);

    if (holeRing.length >= 3) {
      rings.push([...holeRing].reverse());
    }
  });

  // Prefer polylabel — best visual center inside each room polygon.
  try {
    const [x, y] = polylabel(rings, 1.0);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      const pole = { x, y };
      if (isPointInPolygon(pole, polygonVertices)) {
        return pole;
      }
    }
  } catch (e) {
    // fall through
  }

  try {
    const centroid = calculatePolygonCentroid(polygonVertices);
    if (isPointInPolygon(centroid, polygonVertices)) {
      return centroid;
    }
  } catch (e) {
    // ignore bad centroid
  }

  if (isPointInPolygon(bboxCenter, polygonVertices)) {
    return bboxCenter;
  }

  return bboxCenter;
}

function calculateBoundingBox(vertices, conversionFactor) {
  if (vertices.length === 0) {
    return { width: 0, height: 0 };
  }

  // Initialize min and max values with the coordinates of the first vertex
  let minX = vertices[0][0];
  let minY = vertices[0][1];
  let maxX = vertices[0][0];
  let maxY = vertices[0][1];

  // Iterate through the vertices to find the minimum and maximum coordinates
  for (let i = 1; i < vertices.length; i++) {
    const x = vertices[i][0];
    const y = vertices[i][1];

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  // Calculate width and height in meters
  const width = (maxX - minX) * conversionFactor;
  const height = (maxY - minY) * conversionFactor;

  return { width, height };
}

function areaPolygonPoints(area, layer) {
  return area.vertices
    .toArray()
    .map((vertexID) => {
      const vertex = layer.vertices.get(vertexID);
      if (!vertex) return null;
      return [vertex.x, vertex.y];
    })
    .filter((v) => v !== null);
}

function polygonAreaSize(polygon) {
  if (!polygon || polygon.length < 3) return 0;
  return Math.abs(areapolygon(polygon, false));
}

// Outer shell wrapping 2+ rooms: hide its label only (keep room + fill).
function areaIsEnvelopeShell(area, layer) {
  const polygon = areaPolygonPoints(area, layer);
  if (polygon.length < 3) return false;

  const selfArea = polygonAreaSize(polygon);
  if (selfArea <= 0) return false;

  const polygonVertices = polygon.map(([x, y]) => ({ x, y }));
  let childCount = 0;
  let largestChildArea = 0;
  let totalChildArea = 0;

  layer.areas.forEach((other, otherId) => {
    if (otherId === area.id) return;

    const otherPolygon = areaPolygonPoints(other, layer);
    if (otherPolygon.length < 3) return;

    const otherArea = polygonAreaSize(otherPolygon);
    if (otherArea <= 0 || otherArea >= selfArea * 0.92) return;

    const cx =
      otherPolygon.reduce((sum, [x]) => sum + x, 0) / otherPolygon.length;
    const cy =
      otherPolygon.reduce((sum, [, y]) => sum + y, 0) / otherPolygon.length;

    if (isPointInPolygon({ x: cx, y: cy }, polygonVertices)) {
      childCount += 1;
      largestChildArea = Math.max(largestChildArea, otherArea);
      totalChildArea += otherArea;
    }
  });

  return (
    childCount >= 2 &&
    selfArea > largestChildArea * 1.25 &&
    totalChildArea > selfArea * 0.35
  );
}

// Hide label when another area already covers nearly the same room
// (client: stacked "10.11m x 9.20m" / "10.10m x 9.20m").
function isNearDuplicateDimensionArea(area, layer) {
  const polygon = areaPolygonPoints(area, layer);
  if (polygon.length < 3) return false;
  const selfArea = polygonAreaSize(polygon);
  if (selfArea <= 0) return false;
  const cx = polygon.reduce((s, [x]) => s + x, 0) / polygon.length;
  const cy = polygon.reduce((s, [, y]) => s + y, 0) / polygon.length;
  const span = Math.sqrt(selfArea);

  let hide = false;
  layer.areas.forEach((other, otherId) => {
    if (hide || otherId === area.id) return;
    // Keep the "first" area id; hide later duplicates.
    if (String(otherId) > String(area.id)) return;
    const otherPolygon = areaPolygonPoints(other, layer);
    if (otherPolygon.length < 3) return;
    const otherArea = polygonAreaSize(otherPolygon);
    if (otherArea <= 0) return;
    const ratio =
      Math.min(selfArea, otherArea) / Math.max(selfArea, otherArea);
    if (ratio < 0.92) return;
    const ox =
      otherPolygon.reduce((s, [x]) => s + x, 0) / otherPolygon.length;
    const oy =
      otherPolygon.reduce((s, [, y]) => s + y, 0) / otherPolygon.length;
    const dist = Math.hypot(cx - ox, cy - oy);
    if (dist < Math.max(40, span * 0.12)) {
      hide = true;
    }
  });
  return hide;
}
